import Stack from "@mui/material/Stack";
import { ReactHookAutocomplete } from "core/components/ReactHookFormComponents/ReactHookAutocomplete";
import { ReactHookFormSelect } from "core/components/ReactHookFormComponents/ReactHookFormSelect";
import type { SelectOption, TFunction } from "core/entities";
import { useCentrixApiAnonymous } from "core/hooks/useCentrixApiAnonymous";
import { useDevicePosition } from "core/hooks/useDevicePosition";
import { useFormatNumber } from "core/hooks/useFormatNumber";
import { getBrand } from "core/utils/getBrand";
import { isValidRegion } from "core/utils/isValidRegion";
import type { EstimateRequestCustomerInformationFormValues } from "features/EstimateRequest/entities";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";
import type { UseFormReturn } from "react-hook-form";
import { ShopInfoDisplay } from "../../../components/ShopInfoDisplay";
import { getDistanceToShopInMeters } from "./getDistanceToShopInMeters";
import {
  type LocationLookupData,
  useLocationLookup,
} from "./useLocationLookup";

type EstimateRequestShopSelectProps = {
  formMethods: UseFormReturn<EstimateRequestCustomerInformationFormValues>;
  t: TFunction;
  onChangeCallback?: () => void;
};

function determineProvinceStateCodes(
  locationData: LocationLookupData | undefined,
  maxShopDistance: EstimateRequestCustomerInformationFormValues["maxShopDistance"]
): [string] | undefined {
  if (maxShopDistance !== "Provincial") {
    return undefined;
  }
  if (!locationData) {
    return undefined;
  }
  return [
    `${locationData.countryIsoTwoAlphaCode}-${locationData.provinceStateCode}`,
  ];
}

function determineCountryIso2AlphaCodes(
  routerRegion: string | string[] | undefined,
  locationData: LocationLookupData | undefined,
  maxShopDistance: EstimateRequestCustomerInformationFormValues["maxShopDistance"]
): [string] | undefined {
  if (!locationData) {
    return isValidRegion(routerRegion)
      ? [routerRegion.toUpperCase()]
      : undefined;
  }
  // there is location data, so we take the maxShopDistance into account
  if (maxShopDistance !== "National") {
    return undefined;
  }
  if (isValidRegion(routerRegion)) {
    return [routerRegion.toUpperCase()];
  }
  if (isValidRegion(locationData.countryIsoTwoAlphaCode)) {
    return [locationData.countryIsoTwoAlphaCode];
  }
  return undefined;
}

export function EstimateRequestShopSelect({
  formMethods,
  t,
  onChangeCallback,
}: EstimateRequestShopSelectProps) {
  const {
    locale,
    query: { region },
  } = useRouter();
  const countryIso2AlphaCode =
    typeof region === "string" ? region.toUpperCase() : undefined;

  const { data: locationData } = useLocationLookup();
  const maxShopDistance = formMethods.watch("maxShopDistance");

  const CountryIso2AlphaCodes = useMemo(
    () => determineCountryIso2AlphaCodes(region, locationData, maxShopDistance),
    [region, maxShopDistance, locationData]
  );
  const ProvinceStateCodes = useMemo(
    () => determineProvinceStateCodes(locationData, maxShopDistance),
    [locationData, maxShopDistance]
  );
  const { brandCode } = getBrand();

  const { data: shopData, isLoading: isShopDataLoading } =
    useCentrixApiAnonymous({
      path: "/api/shared-entities/shops/anonymous-public",
      parameters: {
        query: {
          UserLanguageCode: locale,
          ShopStatus: "Active",
          BrandShortCodes: [brandCode],
          CountryIso2AlphaCodes,
          ProvinceStateCodes,
        },
      },
      fetchAll: true,
    });
  const { position } = useDevicePosition(true);

  const selectedShopId = formMethods.watch("shopId");

  const selectedShop = shopData?.items?.find(
    (shop) => shop.id?.toLowerCase() === selectedShopId.toLowerCase()
  );

  const isUnitedStates = countryIso2AlphaCode === "US";
  const conversionFactor = isUnitedStates ? 1_609.34 : 1_000;
  const unit = isUnitedStates ? "mile" : "kilometer";

  const formatDistance = useFormatNumber(1, {
    style: "unit",
    unit,
  });

  const metersToFormattedUnit = useCallback(
    (meters: number) => formatDistance(meters / conversionFactor),
    [formatDistance, conversionFactor]
  );

  const distanceOptions = useMemo<
    SelectOption<number | "Provincial" | "National" | "None">[]
  >(
    () => [
      { label: t("none"), value: "None" },
      { label: t("provincial"), value: "Provincial" },
      { label: t("national"), value: "National" },
      { label: formatDistance(25), value: 25 },
      { label: formatDistance(50), value: 50 },
      { label: formatDistance(100), value: 100 },
      { label: formatDistance(250), value: 250 },
      { label: formatDistance(500), value: 500 },
      { label: formatDistance(1000), value: 1000 },
    ],
    [formatDistance, t]
  );

  const shopOptions = useMemo<SelectOption[]>(
    () =>
      shopData?.items
        ?.map((shop) => ({
          ...shop,
          distanceInMeters: getDistanceToShopInMeters(shop, position),
        }))
        .sort((a, b) => {
          if (!a.distanceInMeters && !b.distanceInMeters && a.name && b.name) {
            return a.name > b.name ? 10_000 : -10_000;
          }
          if (!a.distanceInMeters) {
            return 10_000;
          }
          if (!b.distanceInMeters) {
            return -10_000;
          }
          return a.distanceInMeters - b.distanceInMeters;
        })
        .filter((entry) => {
          if (!position) {
            return true;
          }
          // Provincial and National are not numbers in this case the options are pre-filtered by the api
          if (typeof maxShopDistance !== "number") {
            return true;
          }
          return (
            entry.distanceInMeters !== null &&
            entry.distanceInMeters <= maxShopDistance * conversionFactor
          );
        })
        .map((shop) => {
          if (!shop.distanceInMeters) {
            return {
              value: shop.id,
              label: shop.name,
            };
          }
          return {
            value: shop.id?.toLowerCase() ?? "",
            label: `${shop.name} (${metersToFormattedUnit(shop.distanceInMeters)})`,
          };
        })
        .filter((entry): entry is SelectOption =>
          Boolean(
            entry.label &&
              entry.value &&
              typeof entry.label === "string" &&
              typeof entry.value === "string"
          )
        ) ?? [],
    [
      conversionFactor,
      maxShopDistance,
      metersToFormattedUnit,
      position,
      shopData?.items,
    ]
  );
  if (position) {
    return (
      <>
        <Stack direction="row" flexWrap="wrap" gap={2}>
          <ReactHookAutocomplete
            required
            control={formMethods.control}
            name="shopId"
            label={t("shopId")}
            sx={{ flex: 2, minWidth: "min(300px, 100%)" }}
            // Image uploads are tied to the shopId, so we need to reset the images when the shopId changes
            onChangeCallback={onChangeCallback}
            getOptionKey={(option) => option.value as string}
            loading={isShopDataLoading}
            options={shopOptions}
          />
          <ReactHookFormSelect
            sx={{ flex: 1, minWidth: "min(200px, 100%)" }}
            control={formMethods.control}
            name="maxShopDistance"
            label={t("maxShopDistance")}
            options={distanceOptions}
          />
        </Stack>
        {selectedShop && <ShopInfoDisplay shopInfo={selectedShop} />}
      </>
    );
  }
  return (
    <>
      <ReactHookAutocomplete
        required
        control={formMethods.control}
        name="shopId"
        label={t("shopId")}
        // Image uploads are tied to the shopId, so we need to reset the images when the shopId changes
        onChangeCallback={onChangeCallback}
        getOptionKey={(option) => option.value as string}
        loading={isShopDataLoading}
        options={shopOptions}
      />
      {selectedShop && <ShopInfoDisplay shopInfo={selectedShop} />}
    </>
  );
}
